import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import WebIcon from './WebIcon';
import {
  coalesceText,
  mergePayload,
  deriveCardType,
  resolveCardTypeMeta,
  getTypeIcon,
  ORANGE,
  detectYouTubeEmbed,
} from './itineraryUtils';
import styles from './ItineraryEditor.styles';
import PoiAdapter from './PoiAdapter';
import MealAdapter from './MealAdapter';
import LodgingAdapter from './LodgingAdapter';
import TransportAdapter from './TransportAdapter';
import ActivityAdapter from './ActivityAdapter';
import ShoppingAdapter from './ShoppingAdapter';
import FallbackAdapter from './FallbackAdapter';
import NoteAdapter from './NoteAdapter';
import ExternalAdapter from './ExternalAdapter';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
  /** 是否在遊記上下文中渲染（不顯示時間，顯示地址/電話等） */
  isTravelPost?: boolean;
}

const CardRenderer: React.FC<Props> = ({ it, isExpanded = false, onToggleExpand, onOpenPoi, dayDate, onImageClick, isTravelPost = false }) => {
  const mergedPayload = mergePayload(it);
  
  // Check if this item has video content (explicit embed or YouTube link)
  // If so, we force the ExternalAdapter which knows how to render the video player.
  const website = mergedPayload?.website || mergedPayload?.url || it?.website;
  const hasVideo = Boolean(mergedPayload?.embed?.src) || Boolean(detectYouTubeEmbed(website));

  const baseType = typeof it?.type === 'string' ? it.type.toLowerCase() : '';
  const derivedType = deriveCardType(it);
  const genericTypes = new Set(['', 'activity', 'poi', 'place', 'event', 'itinerary', 'item', 'custom']);
  const preferDerived = genericTypes.has(baseType) && (!genericTypes.has(derivedType) || baseType === '');
  const cardType = preferDerived ? derivedType : (baseType || derivedType);
  
  // Debug logging for all cards
  console.log('[CardRenderer] Card type decision:', {
    title: it?.title,
    baseType,
    derivedType,
    finalCardType: cardType,
    hasVideo,
    hasCard: !!it?.card,
    cardCardType: it?.card?.card_type,
    itType: it?.type,
    willUseFallback: !['note', 'external', 'poi', 'place', 'restaurant', 'meal', 'lodging', 'hotel', 'accommodation', 'activity', 'shopping', 'mall', 'store', 'market', 'transport', 'flight', 'train', 'bus'].includes(cardType)
  });

  if (cardType === 'note' || baseType === 'note') return <NoteAdapter it={it} onImageClick={onImageClick} />;

  // simple routing to adapters
  if (!it?.card && !(it?.payload && typeof it.payload === 'object')) {
    return (
      <View style={styles.legacyCardContainer}>
        <Text style={styles.cardTitle}>{it?.title || it?.name || '未命名'}</Text>
        {it?.note ? <Text style={styles.cardDescription}>{it.note}</Text> : null}
      </View>
    );
  }

  if (hasVideo || cardType === 'external' || cardType === 'video') return <ExternalAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
  if (cardType === 'poi' || cardType === 'place' || cardType === 'osm') return <PoiAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
  if (cardType === 'restaurant' || cardType === 'meal') return <MealAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
  if (cardType === 'lodging' || cardType === 'hotel' || cardType === 'accommodation') return <LodgingAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
  if (cardType === 'activity') return <ActivityAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
  if (cardType === 'shopping' || cardType === 'mall' || cardType === 'store' || cardType === 'market') return <ShoppingAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
  if (cardType === 'transport' || cardType === 'flight' || cardType === 'train' || cardType === 'bus') return <TransportAdapter it={it} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;

  return <FallbackAdapter it={it} isExpanded={isExpanded} onToggleExpand={onToggleExpand} onOpenPoi={onOpenPoi} dayDate={dayDate} onImageClick={onImageClick} isTravelPost={isTravelPost} />;
};

export default CardRenderer;
